import React, { useEffect, useState, useCallback } from 'react';
import '../../styles/CookieConsent.css';

const CONSENT_VERSION = 2;
const CONSENT_TTL_MS = 1000 * 60 * 60 * 24 * 365;
const STORAGE_KEY = 'cookieConsent';

export type ConsentCategories = {
  necessary: true;
  externalMedia: boolean;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = ConsentCategories & {
  ts: number;
  version: number;
};

const DEFAULT_CONSENT: ConsentCategories = {
  necessary: true,
  externalMedia: false,
  analytics: false,
  marketing: false,
};

function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;

    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CONSENT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistConsent(value: ConsentCategories): StoredConsent {
  const stored: StoredConsent = {
    ...value,
    necessary: true,
    ts: Date.now(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  return stored;
}

export function getCurrentConsent(): ConsentCategories {
  const stored = readConsent();
  if (!stored) return DEFAULT_CONSENT;
  return {
    necessary: true,
    externalMedia: !!stored.externalMedia,
    analytics: !!stored.analytics,
    marketing: !!stored.marketing,
  };
}

export function hasConsent(category: keyof ConsentCategories): boolean {
  const c = getCurrentConsent();
  return !!c[category];
}

function dispatchConsentChange(consent: ConsentCategories): void {
  window.dispatchEvent(
    new CustomEvent<ConsentCategories>('consent-change', { detail: consent })
  );
}

function activateConsentedScripts(consent: ConsentCategories): void {
  const activate = (category: 'analytics' | 'marketing') => {
    if (!consent[category]) return;
    const nodes = document.querySelectorAll<HTMLScriptElement>(
      `script[data-consent="${category}"][type="text/plain"]`
    );
    nodes.forEach((node) => {
      const replacement = document.createElement('script');
      Array.from(node.attributes).forEach((attr) => {
        if (attr.name !== 'type') replacement.setAttribute(attr.name, attr.value);
      });
      replacement.textContent = node.textContent || '';
      node.replaceWith(replacement);
    });
  };

  activate('analytics');
  activate('marketing');
}

const CookieConsent: React.FC = () => {
  const [hasDecision, setHasDecision] = useState<boolean>(() => readConsent() !== null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [analyticsChecked, setAnalyticsChecked] = useState<boolean>(false);
  const [marketingChecked, setMarketingChecked] = useState<boolean>(false);
  const [externalMediaChecked, setExternalMediaChecked] = useState<boolean>(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setAnalyticsChecked(!!stored.analytics);
      setMarketingChecked(!!stored.marketing);
      setExternalMediaChecked(!!stored.externalMedia);
      activateConsentedScripts(stored);
      dispatchConsentChange(stored);
    }
  }, []);

  const saveAndClose = useCallback((value: ConsentCategories) => {
    const stored = persistConsent(value);
    setAnalyticsChecked(!!stored.analytics);
    setMarketingChecked(!!stored.marketing);
    setExternalMediaChecked(!!stored.externalMedia);
    setHasDecision(true);
    setIsModalOpen(false);
    activateConsentedScripts(stored);
    dispatchConsentChange(stored);
  }, []);

  const acceptAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      externalMedia: true,
      analytics: true,
      marketing: true,
    });
  }, [saveAndClose]);

  const rejectAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      externalMedia: false,
      analytics: false,
      marketing: false,
    });
  }, [saveAndClose]);

  const saveSelection = useCallback(() => {
    saveAndClose({
      necessary: true,
      externalMedia: externalMediaChecked,
      analytics: analyticsChecked,
      marketing: marketingChecked,
    });
  }, [externalMediaChecked, analyticsChecked, marketingChecked, saveAndClose]);

  useEffect(() => {
    const handleOpen = () => {
      const stored = readConsent();
      if (stored) {
        setAnalyticsChecked(!!stored.analytics);
        setMarketingChecked(!!stored.marketing);
        setExternalMediaChecked(!!stored.externalMedia);
      }
      setIsModalOpen(true);
    };
    window.addEventListener('open-cookie-settings', handleOpen);
    return () => window.removeEventListener('open-cookie-settings', handleOpen);
  }, []);

  const showBanner = !hasDecision && !isModalOpen;

  if (!showBanner && !isModalOpen) return null;

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-modal="false"
          aria-live="polite"
          aria-label="Cookie-Hinweis"
          className="cc-banner"
        >
          <div className="cc-banner-content">
            <h3 className="cc-banner-title">Cookies &amp; Datenschutz</h3>
            <p className="cc-banner-text">
              Wir verwenden Cookies und vergleichbare Technologien. Technisch notwendige Cookies
              sind für den Betrieb der Website erforderlich. Mit Ihrer Einwilligung verwenden wir
              auch Cookies für externe Medien (z.&nbsp;B. Karten, Videos), Analyse und
              Marketing. Sie können Ihre Einwilligung jederzeit widerrufen. Weitere
              Informationen finden Sie in unserer{' '}
              <a
                href="/datenschutz"
                className="cc-privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Datenschutzerklärung
              </a>{' '}
              und im{' '}
              <a
                href="/impressum"
                className="cc-privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Impressum
              </a>
              .
            </p>
          </div>
          <div className="cc-actions">
            <button type="button" className="cc-btn cc-equal" onClick={rejectAll}>
              Alle ablehnen
            </button>
            <button
              type="button"
              className="cc-btn cc-equal cc-tertiary"
              onClick={() => setIsModalOpen(true)}
            >
              Einstellungen
            </button>
            <button type="button" className="cc-btn cc-equal cc-primary" onClick={acceptAll}>
              Alle akzeptieren
            </button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div
          className="cc-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && hasDecision) {
              setIsModalOpen(false);
            }
          }}
        >
          <div className="cc-modal-card" onClick={(e) => e.stopPropagation()}>
            {hasDecision && (
              <button
                type="button"
                className="cc-modal-close"
                onClick={() => setIsModalOpen(false)}
                aria-label="Schließen"
              >
                ×
              </button>
            )}
            <h2 id="cc-modal-title">Cookie-Einstellungen</h2>
            <p className="cc-modal-intro">
              Hier können Sie selbst entscheiden, welche Cookies und Technologien wir verwenden
              dürfen. Sie können Ihre Auswahl jederzeit ändern oder widerrufen. Weitere
              Informationen finden Sie in unserer{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                Datenschutzerklärung
              </a>
              .
            </p>

            <div className="cc-cookie-category">
              <label className="cc-category-header">
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="cc-checkbox-input"
                  aria-label="Notwendige Cookies (immer aktiv)"
                />
                <div className="cc-category-info">
                  <strong>Notwendige Cookies</strong>
                  <span className="cc-category-badge">Immer aktiv</span>
                </div>
              </label>
              <p className="cc-category-description">
                Diese Cookies sind für den Betrieb der Website unbedingt erforderlich (z.&nbsp;B.
                Speicherung Ihrer Cookie-Einstellungen). Sie können nicht deaktiviert werden.
                Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TDDDG, Art. 6 Abs. 1 lit. f DSGVO.
              </p>
            </div>

            <div className="cc-cookie-category">
              <label className="cc-category-header">
                <input
                  type="checkbox"
                  checked={externalMediaChecked}
                  onChange={(e) => setExternalMediaChecked(e.target.checked)}
                  className="cc-checkbox-input"
                  aria-label="Externe Medien"
                />
                <div className="cc-category-info">
                  <strong>Externe Medien</strong>
                </div>
              </label>
              <p className="cc-category-description">
                Erlaubt das Laden externer Inhalte wie <strong>Google Maps</strong> (Karten) und{' '}
                <strong>YouTube</strong> (Videos). Beim Laden dieser Inhalte werden Daten
                (insbesondere Ihre IP-Adresse) an den jeweiligen Anbieter (Google Ireland Ltd. /
                Google LLC, USA) übertragen. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO,
                § 25 Abs. 1 TDDDG.
              </p>
            </div>

            <div className="cc-cookie-category">
              <label className="cc-category-header">
                <input
                  type="checkbox"
                  checked={analyticsChecked}
                  onChange={(e) => setAnalyticsChecked(e.target.checked)}
                  className="cc-checkbox-input"
                  aria-label="Analyse-Cookies"
                />
                <div className="cc-category-info">
                  <strong>Analyse-Cookies</strong>
                </div>
              </label>
              <p className="cc-category-description">
                Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website
                interagieren, indem sie Informationen anonym sammeln und melden. Auf Grundlage
                Ihrer Einwilligung. Aktuell sind keine Analyse-Tools im Einsatz.
              </p>
            </div>

            <div className="cc-cookie-category">
              <label className="cc-category-header">
                <input
                  type="checkbox"
                  checked={marketingChecked}
                  onChange={(e) => setMarketingChecked(e.target.checked)}
                  className="cc-checkbox-input"
                  aria-label="Marketing-Cookies"
                />
                <div className="cc-category-info">
                  <strong>Marketing-Cookies</strong>
                </div>
              </label>
              <p className="cc-category-description">
                Diese Cookies werden eingesetzt, um Besuchern relevante Werbeanzeigen
                bereitzustellen. Aktuell sind keine Marketing-Tools im Einsatz.
              </p>
            </div>

            <div className="cc-modal-actions">
              <button type="button" className="cc-btn cc-equal" onClick={rejectAll}>
                Alle ablehnen
              </button>
              <button type="button" className="cc-btn cc-equal cc-tertiary" onClick={saveSelection}>
                Auswahl speichern
              </button>
              <button type="button" className="cc-btn cc-equal cc-primary" onClick={acceptAll}>
                Alle akzeptieren
              </button>
            </div>

            <p className="cc-modal-footer">
              Verantwortlicher: IB Neubaur GmbH, Nelkenweg 6a, 86641 Rain am Lech.
              Sie können Ihre Einwilligung jederzeit über den Link „Cookie-Einstellungen" im
              Footer anpassen oder widerrufen. Weitere Informationen in unserer{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                Datenschutzerklärung
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
